import { useRef, useState } from 'react';

export type Pagination = {
    firstPage: number;
    currentPage: number;
    itemsCount: number;
    itemsPerPage: number;
    lastPage: number;
};

export type PaginationProps<ItemType> = {
    fetcher: (params: {
        itemsPerPage: PaginationState<ItemType>['itemsPerPage'];
        page: number;
    }) => Promise<{ items: ItemType[]; pagination: Pagination }>;
    items: ItemType[];
    pagination: Pagination;
    manuallyHandlePageChangeError?: boolean; // Use it if you handle pagination from query params / URL.
};

export type PaginationState<ItemType> = {
    allItems: () => Array<ItemType>;
    currentPage: number;
    currentPageItems: () => Array<ItemType>;
    firstPage: number;
    goToPage: (page: number) => Promise<void>;
    goToNextPage: () => Promise<void>;
    goToPreviousPage: () => Promise<void>;
    isAnyPageLoading: () => boolean;
    isCurrentPageLoading: () => boolean;
    isPageLoading: (page: number) => boolean;
    itemsCount: number;
    itemsPerPage: number;
    lastPage: number;
    nextPage: number | null;
    pageItems: (page: number) => Array<ItemType>;
    previousPage: number | null;
    reset: (pagination: Pagination) => void;
};

export function usePagination<ItemType>({
    fetcher,
    items: initialItems,
    pagination: initialPagination,
    manuallyHandlePageChangeError = false,
}: PaginationProps<ItemType>): PaginationState<ItemType> {
    const dataMapRef = useRef<Map<number, Array<ItemType>>>(
        new Map([[initialPagination.currentPage, initialItems]]),
    );
    const [fetchingPages, setFetchingPages] = useState<number[]>([]);
    // @todo: is it really necessary?
    // I had a double fetch when cleaning ItineraryList page, but maybe the cause is elsewhere.
    const fetchIngPagesRef = useRef(fetchingPages);
    const [pagination, setPagination] = useState(initialPagination);

    const nextPage =
        pagination.currentPage < pagination.lastPage ? pagination.currentPage + 1 : null;
    const previousPage =
        pagination.currentPage > pagination.firstPage ? pagination.currentPage - 1 : null;

    function updateFetchingPages(fetchingPages: Array<number>) {
        fetchIngPagesRef.current = fetchingPages;
        setFetchingPages(fetchingPages);
    }

    async function goToPreviousPage() {
        if (previousPage === null) {
            throw new Error('Pagination cannot go to previous page when already on first page');
        }
        return goToPage(previousPage);
    }

    async function goToNextPage() {
        if (nextPage === null) {
            throw new Error('Pagination cannot go to next page when already on last page');
        }
        return goToPage(nextPage);
    }

    function goToPage(page: number): Promise<void> {
        const pageBeforeChanging = pagination.currentPage;

        return new Promise((resolve, reject) => {
            if (page < pagination.firstPage) {
                return reject(new Error(`Pagination cannot go to a page lower than first page`));
            }

            if (page > pagination.lastPage) {
                return reject(new Error(`Pagination cannot go to a page higher than last page`));
            }

            setPagination((prevPagination) => {
                return {
                    ...prevPagination,
                    currentPage: page,
                };
            });

            if (dataMapRef.current.get(page)) {
                return resolve();
            }

            updateFetchingPages(fetchingPages.concat([page]));

            fetcher({ itemsPerPage: pagination.itemsPerPage, page })
                .then(({ items, pagination }) => {
                    dataMapRef.current.set(page, items);
                    setPagination(pagination);
                    updateFetchingPages(
                        fetchingPages.filter((pageFetching) => {
                            return pageFetching !== page;
                        }),
                    );
                    resolve();
                })
                .catch((error: Error) => {
                    updateFetchingPages(
                        fetchingPages.filter((pageFetching) => {
                            return pageFetching !== page;
                        }),
                    );

                    if (!manuallyHandlePageChangeError) {
                        setPagination((prevPagination) => {
                            return {
                                ...prevPagination,
                                currentPage: pageBeforeChanging,
                            };
                        });
                    }

                    reject(error);
                });
        });
    }

    function reset(pagination: Pagination) {
        dataMapRef.current.clear();
        updateFetchingPages([]);
        setPagination(pagination);
    }

    function allItems(): ItemType[] {
        const pageNumbers = Array.from(dataMapRef.current.keys()).sort(sortPagesByAsc);

        return pageNumbers.reduce<ItemType[]>((allPagesData, page) => {
            const pageData = dataMapRef.current.get(page);
            if (pageData) {
                return allPagesData.concat(pageData);
            }
            return allPagesData;
        }, []);
    }

    function pageItems(page: number): ItemType[] {
        return dataMapRef.current.get(page) ?? [];
    }

    function currentPageItems(): ItemType[] {
        return pageItems(pagination.currentPage);
    }

    function isAnyPageLoading(): boolean {
        return fetchIngPagesRef.current.length > 0;
    }

    function isCurrentPageLoading(): boolean {
        return isPageLoading(pagination.currentPage);
    }

    function isPageLoading(page: number): boolean {
        return fetchIngPagesRef.current.includes(page);
    }

    return {
        allItems,
        currentPage: pagination.currentPage,
        currentPageItems,
        firstPage: pagination.firstPage,
        goToPage,
        goToNextPage,
        goToPreviousPage,
        isAnyPageLoading,
        isCurrentPageLoading,
        isPageLoading,
        itemsCount: pagination.itemsCount,
        itemsPerPage: pagination.itemsPerPage,
        lastPage: pagination.lastPage,
        nextPage,
        pageItems,
        previousPage,
        reset,
    };
}

function sortPagesByAsc(pageA: number, pageB: number) {
    if (pageA < pageB) return -1;
    if (pageA > pageB) return 1;
    return 0;
}
